import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Headset, CheckCircle2, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CommentField } from "@/components/CommentField";
import { saveUnlock } from "@/lib/unlock";

const inputCls = "bg-slate-950 border-slate-800 focus:border-brand text-slate-100";

export default function SpecialistRequestDialog({ open, onOpenChange, vehicle, dealer, onLead }) {
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open && dealer?.id) {
      api.post("/public/specialist-interest", { vehicle_id: vehicle?.id, dealer_id: dealer.id }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(() => { setDone(false); setForm({ customer_name: "", phone: "", email: "", message: "" }); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Add a phone number or email so a specialist can reach you");
      return;
    }
    setBusy(true);
    try {
      const { data: lead } = await api.post("/public/leads", {
        vehicle_id: vehicle?.id,
        dealer_id: dealer?.id,
        source: "specialist_request",
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        comment: form.message,
        message: `Asked to talk to a specialist${vehicle ? ` about the ${vehicle.year} ${vehicle.make} ${vehicle.model}` : ""}.`,
      });
      if (dealer?.id) saveUnlock(dealer.id, lead);
      onLead?.(lead);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="specialist-request-dialog" style={brandStyle(dealer?.accent_color)} className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <Headset className="h-5 w-5 text-brand" /> Talk to a specialist
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Leave your details and {dealer?.name || "the dealership"} will have a product specialist reach out{vehicle ? ` about the ${vehicle.year} ${vehicle.model}` : ""}.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div data-testid="specialist-request-success" className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="font-heading text-xl font-bold">You're all set, {form.customer_name.split(" ")[0]}!</h3>
            <p className="text-sm text-slate-400 mt-2">A specialist from {dealer?.name} will be in touch shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-slate-300 mb-1.5 block">Your name</Label>
              <Input required data-testid="request-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 mb-1.5 block">Mobile</Label>
                <Input type="tel" data-testid="request-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-0100" className={inputCls} />
              </div>
              <div>
                <Label className="text-slate-300 mb-1.5 block">Email</Label>
                <Input type="email" data-testid="request-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <CommentField label="Anything we should know? (optional)" value={form.message} onChange={(message) => setForm({ ...form, message })} placeholder="Best time to call, trade-in, financing questions…" testId="request-message" />
            <Button type="submit" disabled={busy || !form.customer_name.trim() || (!form.phone.trim() && !form.email.trim())} data-testid="request-submit-btn" className="w-full h-11 bg-brand hover:bg-brand/90 font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request a call <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
            <p className="text-[11px] text-slate-500 text-center">Phone or email — at least one. We only share this with {dealer?.name || "the dealership"}.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
